import { LinkOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { Drawer, Flex, Spin, Tag } from 'antd';
import { type FC, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type {
  AnalyticsWidgetStatsRequest,
  WidgetPageStats,
  WidgetPreviewModeSummary,
} from '@shared/api';
import { useGetWidgetPageMessagesQuery } from '@shared/api/widget-pages-api';
import { Routes } from '@shared/constants/routes';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';

import {
  NO_ESCAPE,
  formatCount,
  formatPointsDelta,
  formatPotential,
  formatRate,
  getDeltaPercent,
  getPreviewStatus,
} from '../../lib/widget-pages';
import { DeltaBadge, PreviewStatusTag } from './widget-pages-bits';
import styles from './widget-pages.module.scss';

const MESSAGES_LIMIT = 10;

type Props = {
  page: WidgetPageStats | null;
  request: AnalyticsWidgetStatsRequest | null;
  previewMode: WidgetPreviewModeSummary | null | undefined;
  lowDataVisitors: number;
  onClose: () => void;
};

type StatTileProps = {
  label: string;
  value: string;
  sub?: ReactNode;
  delta?: ReactNode;
};

const StatTile: FC<StatTileProps> = ({ label, value, sub, delta }) => (
  <div className={styles.statTile}>
    <div className={styles.statLabel}>{label}</div>
    <Flex align="baseline" gap={6}>
      <span className={styles.statValue}>{value}</span>
      {delta}
    </Flex>
    {sub !== undefined && <div className={styles.statSub}>{sub}</div>}
  </div>
);

const PointsDelta: FC<{ current: number; previous: number | null | undefined }> = ({
  current,
  previous,
}) => {
  const text = formatPointsDelta(current, previous);
  if (!text) return null;
  const className = text.startsWith('+')
    ? styles.deltaUp
    : text.startsWith('−')
      ? styles.deltaDown
      : undefined;
  return <span className={`${styles.delta} ${className ?? ''}`}>{text}</span>;
};

export const WidgetPageDrawer: FC<Props> = ({
  page,
  request,
  previewMode,
  lowDataVisitors,
  onClose,
}) => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'analytics.widget.pages.drawer',
  });
  const locale = i18n.language;

  // Keep the last page while the drawer slides out so the content does not vanish mid-animation.
  const [shown, setShown] = useState<WidgetPageStats | null>(page);
  useEffect(() => {
    if (page) setShown(page);
  }, [page]);

  const messages = useGetWidgetPageMessagesQuery(
    page && request
      ? {
          ...request,
          page_host: page.page_host,
          page_path: page.page_path,
          limit: MESSAGES_LIMIT,
        }
      : skipToken,
  );

  const renderBenchmark = (item: WidgetPageStats) => {
    if (item.low_data) {
      return <span>{t('lowData', { count: lowDataVisitors })}</span>;
    }
    if (item.benchmark_lead_rate === null || item.benchmark_lead_rate === undefined) {
      return <span>{t('benchmark.none')}</span>;
    }
    const level = item.benchmark_level ?? 'all';
    return (
      <>
        <span>
          {t(`benchmark.${level}`, {
            segment: level === 'host' ? item.page_host : item.segment,
            rate: formatRate(item.benchmark_lead_rate),
            ...NO_ESCAPE,
          })}
        </span>
        <span className={styles.benchmarkPotential}>
          {item.potential_leads > 0
            ? t('potentialPositive', {
                value: formatPotential(item.potential_leads),
                visitors: formatCount(item.visitors, locale),
                benchmark: formatRate(item.benchmark_lead_rate),
                rate: formatRate(item.lead_rate),
              })
            : t('potentialNone')}
        </span>
      </>
    );
  };

  const renderMessages = (item: WidgetPageStats) => {
    if (messages.isLoading || messages.isFetching) {
      return (
        <div className={styles.blockLoading}>
          <Spin size="small" />
        </div>
      );
    }
    if (messages.isError) return <FetchingErrorState refetch={messages.refetch} />;
    const items = messages.data?.items ?? [];
    if (items.length === 0) {
      return <span className={styles.blockHint}>{t('messagesEmpty')}</span>;
    }
    return (
      <ul className={styles.messageList}>
        {items.map((message) => {
          const share = item.first_message ? (message.visitors / item.first_message) * 100 : 0;
          return (
            <li key={message.message} className={styles.messageRow}>
              <span className={styles.messageText} title={message.message}>
                {message.message}
              </span>
              <span className={styles.messageMeta}>
                <span className={styles.value}>{message.visitors}</span>
                <span className={styles.secondary}>{formatRate(share)}</span>
              </span>
              <span className={styles.messageBar}>
                <span style={{ width: `${Math.min(100, share)}%` }} />
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderPreview = (item: WidgetPageStats) => {
    const status = getPreviewStatus(item, previewMode);
    if (status === 'static') return <span className={styles.blockHint}>{t('previewStatic')}</span>;
    if (!item.preview) return <span className={styles.blockHint}>{t('previewNone')}</span>;
    return (
      <div className={styles.previewCard}>
        <Flex align="center" justify="space-between" gap={12}>
          <PreviewStatusTag status={status} />
          <Link to={Routes.preview.root} className={styles.drawerLink}>
            {t('openPreviews')}
          </Link>
        </Flex>
        <p className={styles.previewQuestion}>{item.preview.question}</p>
        <div className={styles.previewAnswers}>
          {item.preview.answers.map((answer) => (
            <Tag bordered={false} key={answer}>
              {answer}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      open={Boolean(page)}
      onClose={onClose}
      width={640}
      styles={{ body: { padding: 20 } }}
      title={
        shown && (
          <div className={styles.drawerHeader}>
            <span className={styles.drawerTitle}>{shown.page_path}</span>
            <a
              href={shown.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.drawerLink}
            >
              {shown.page_host}
              <LinkOutlined />
            </a>
          </div>
        )
      }
    >
      {shown && (
        <Flex vertical gap={20}>
          <div className={styles.statGrid}>
            <StatTile
              label={t('visitors')}
              value={formatCount(shown.visitors, locale)}
              delta={
                <DeltaBadge delta={getDeltaPercent(shown.visitors, shown.previous?.visitors)} />
              }
              sub={
                shown.previous
                  ? t('previousPeriod', { value: formatCount(shown.previous.visitors, locale) })
                  : t('noPreviousPeriod')
              }
            />
            <StatTile
              label={t('chat')}
              value={formatRate(shown.chat_rate)}
              delta={<PointsDelta current={shown.chat_rate} previous={shown.previous?.chat_rate} />}
              sub={t('people', { count: shown.chat_open })}
            />
            <StatTile
              label={t('messages')}
              value={formatRate(shown.message_rate)}
              sub={t('people', { count: shown.first_message })}
            />
            <StatTile
              label={t('leads')}
              value={String(shown.leads)}
              delta={<PointsDelta current={shown.lead_rate} previous={shown.previous?.lead_rate} />}
              sub={t('leadRate', { rate: formatRate(shown.lead_rate) })}
            />
          </div>

          <div className={styles.benchmark}>{renderBenchmark(shown)}</div>

          <section className={styles.block}>
            <span className={styles.blockTitle}>{t('messagesTitle')}</span>
            <span className={styles.blockHint}>{t('messagesHint')}</span>
            {renderMessages(shown)}
          </section>

          <section className={styles.block}>
            <span className={styles.blockTitle}>{t('previewTitle')}</span>
            {renderPreview(shown)}
          </section>
        </Flex>
      )}
    </Drawer>
  );
};
